import { motion } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Shield, Scale, Award, CheckCircle, Users, BookOpen } from 'lucide-react';

export const TrustAuthoritySection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1);
  
  // Keep ONLY the actual licenses/certifications that Mr. Fahad Al Tamimi actually holds
  const recognitions = [
    {
      title: 'Saudi Bar Association',
      icon: Shield,
      description: 'Member since 2015',
    },
    // {
    //   title: 'Ministry of Justice License',
    //   icon: Scale,
    //   description: 'License No. 12345',
    // },
    // Add other actual licenses here
  ];

  return (
    <section
      id="credentials"
      aria-label="Legal Credentials"
      className="relative bg-gradient-to-b from-[#faf9f7] to-white py-20 sm:py-24 lg:py-32 overflow-hidden w-full"
    >
      {/* Refined Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #c3a565 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent opacity-40" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent opacity-40" />

      <div
        ref={ref}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        {/* Header Section - Improved Spacing */}
        <div className="max-w-3xl mx-auto text-center mb-16 sm:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span  className="inline-block text-xs tracking-[0.3em] uppercase text-[#c3a565] font-semibold mb-3">
              Legal Credentials
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[#2b2b2a] text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4"
          >
            Bar Admissions & 
            <span className="block font-semibold text-[#c3a565]">Professional Standing</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-[#2b2b2a]/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
          >
            Licensed legal professionals with Saudi Bar Association credentials and 
            recognized standing before Saudi courts and regulatory authorities.
          </motion.p>
        </div>

        {/* Credentials Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-xl border border-[#2b2b2a]/5 overflow-hidden">
            {/* Inner Container with Proper Padding */}
            <div className="p-8 sm:p-10 lg:p-12">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                {recognitions.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isVisible ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                      className="group relative"
                    >
                      <div className="flex items-start gap-5 p-6 rounded-2xl bg-gradient-to-br from-white to-[#faf9f7] border border-[#2b2b2a]/5 hover:border-[#c3a565]/30 hover:shadow-lg transition-all duration-300">
                        {/* Icon Container */}
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#c3a565] rounded-xl blur-md opacity-20 group-hover:opacity-30 transition-opacity" />
                            <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-[#c3a565] to-[#d4b97b] flex items-center justify-center shadow-md">
                              <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-[#2b2b2a] mb-1.5">
                            {item.title}
                          </h3>
                          {item.description && (
                            <p className="text-sm text-[#2b2b2a]/60">
                              {item.description}
                            </p>
                          )}
                          
                          {/* Optional Verified Badge */}
                          <div className="flex items-center gap-1.5 mt-3">
                            <CheckCircle className="w-4 h-4 text-[#c3a565]" />
                            <span className="text-xs text-[#2b2b2a]/50 uppercase tracking-wider">
                              Verified Credential
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Additional Info Section with Proper Spacing */}
              <div className="mt-12 pt-8 border-t border-[#2b2b2a]/10">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-3 text-[#2b2b2a]/70">
                    <Scale className="w-5 h-5 text-[#c3a565]" />
                    <span className="text-sm">Full rights of audience before all Saudi courts</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#2b2b2a]/70">
                    <Users className="w-5 h-5 text-[#c3a565]" />
                    <span className="text-sm">Member of the Saudi Legal Profession</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section - "Talk to us?" with Proper Spacing */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="text-center mt-16 sm:mt-20"
        >
          <div className="inline-flex flex-col items-center">
            <span className="text-sm uppercase tracking-[0.3em] text-[#c3a565] font-medium mb-3">
              Ready to connect?
            </span>
            <button className="group relative px-8 py-4 bg-[#2b2b2a] text-white rounded-full overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-[#2b2b2a]/20">
              <span className="relative z-10 text-lg font-medium flex items-center gap-3">
                Talk to us
                <span className="text-[#c3a565] group-hover:translate-x-1 transition-transform">→</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#c3a565] to-[#d4b97b] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
            <p className="text-sm text-[#2b2b2a]/50 mt-4">
              Schedule a confidential consultation
            </p>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};